import { useMemo } from "react";
import type { FilterState } from "@/types/filters";
import type { SchedulingRow } from "@/types/scheduling";
import { applyBaseFilter, filterRowsByDateRange, filterRowsByScope } from "@/lib/metrics";
import {
  getBucketKey,
  getBucketLabel,
  getPeriodFamily,
  isBucketIncluded,
  type PeriodFamily,
} from "@/lib/periods";

export interface RevenueTrendPoint {
  bucketKey: string;
  label: string;
  totalMedRev: number;
  dvmDays: number;
  medRevPerDvmDay: number;
  medRevPerSeenHour: number;
  medRevPerSchedHour: number;
  medRevPerAppt: number;
}

export interface RevenueTrendData {
  points: RevenueTrendPoint[];
  // NVA Avg. benchmark as a per-bucket series, index-aligned to `points` and
  // bucketed at the same granularity. A flat company-wide average can't tell
  // you whether a dip was yours or everyone's; this can, because each point
  // compares against the company on that same day/week/month.
  // null where the company has no rows in that bucket.
  nvaTotalMedRev: Array<number | null>;
  nvaMedRevPerDvmDay: Array<number | null>;
}

// One doctor working one date = one DVM-day. Counted distinctly so a doctor
// splitting a day across two sites (two scheduling_fact rows) isn't counted twice.
function dvmDayKey(r: SchedulingRow): string {
  return `${r.resource_id}|${r.schedule_date}`;
}

function buildPoints(
  rows: SchedulingRow[],
  granularity: "day" | "week" | "month",
  family: PeriodFamily,
  range: { startDate: string; endDate: string }
): RevenueTrendPoint[] {
  const bucketMap = new Map<string, SchedulingRow[]>();
  for (const row of rows) {
    const key = getBucketKey(row.schedule_date, granularity, family);
    // See useTrendData: calendar periods drop their partial edge weeks so no
    // point on the chart is a stub drawn the size of a full week.
    if (!isBucketIncluded(key, granularity, family, range)) continue;
    if (!bucketMap.has(key)) bucketMap.set(key, []);
    bucketMap.get(key)!.push(row);
  }

  return Array.from(bucketMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, bRows]) => {
      const totalMedRev = bRows.reduce((s, r) => s + (Number(r.medical_net_sales_amount) || 0), 0);
      const scheduledMin = bRows.reduce((s, r) => s + (Number(r.scheduled_minutes) || 0), 0);
      const apptMin = bRows.reduce((s, r) => s + (Number(r.completed_appointment_minutes) || 0), 0);
      const completed = bRows.reduce((s, r) => s + (Number(r.completed_appointments) || 0), 0);
      const dvmDays = new Set(bRows.map(dvmDayKey)).size;

      return {
        bucketKey: key,
        label: getBucketLabel(key, granularity),
        totalMedRev,
        dvmDays,
        medRevPerDvmDay: dvmDays > 0 ? totalMedRev / dvmDays : 0,
        medRevPerSeenHour: apptMin > 0 ? totalMedRev / (apptMin / 60) : 0,
        medRevPerSchedHour: scheduledMin > 0 ? totalMedRev / (scheduledMin / 60) : 0,
        medRevPerAppt: completed > 0 ? totalMedRev / completed : 0,
      };
    });
}

export function useRevenueTrendData(
  rows: SchedulingRow[],
  filters: FilterState,
  startDate: string,
  endDate: string,
  granularity: FilterState["revGranularity"]
): RevenueTrendData {
  return useMemo(() => {
    const base = applyBaseFilter(rows, filters.excludeZeroApptDays);
    const dated = filterRowsByDateRange(base, startDate, endDate);
    const scoped = filterRowsByScope(dated, filters.scope);

    const family = getPeriodFamily(filters.period);
    const range = { startDate, endDate };
    const points = buildPoints(scoped, granularity, family, range);

    // Bucket the unscoped rows identically, then align by bucket key so each
    // benchmark point sits on the same date as the scoped point above it.
    const nvaByKey = new Map(
      buildPoints(dated, granularity, family, range).map((p) => [p.bucketKey, p])
    );
    const nvaTotalMedRev = points.map((p) => nvaByKey.get(p.bucketKey)?.totalMedRev ?? null);
    const nvaMedRevPerDvmDay = points.map(
      (p) => nvaByKey.get(p.bucketKey)?.medRevPerDvmDay ?? null
    );

    return { points, nvaTotalMedRev, nvaMedRevPerDvmDay };
  }, [rows, filters, startDate, endDate, granularity]);
}
